import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useGetMyCardsBoostLink } from '@sorare/core/src/hooks/my-cards/useGetMyCardsBoostLink';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity';
import { getSportFromSportOrShared } from '@sorare/core/src/lib/sports';

import { DumbNotification } from 'components/activity/DumbNotification';

import { commonNotificationInterfaceFragment } from '../fragments';
import type { CommonNotificationProps } from '../types';
import type { InGameCurrencyNotification_inGameCurrencyNotification } from './__generated__/index.graphql';

type Props = CommonNotificationProps & {
  notification: InGameCurrencyNotification_inGameCurrencyNotification;
};

export const InGameCurrencyNotification = ({
  notification,
  ...rest
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const getMyCardsBoostLink = useGetMyCardsBoostLink();

  const { name, createdAt, sport, read, inGameCurrencyBalance } = notification;

  if (!inGameCurrencyBalance.currency) return null;

  const rarity = inGameCurrencyToRarity[inGameCurrencyBalance.currency];

  if (!rarity) return null;

  const link = getMyCardsBoostLink({
    rarity,
    sport: getSportFromSportOrShared(sport) || Sport.FOOTBALL,
  });

  return (
    <DumbNotification
      name={name}
      title={<LazyMarkdown data={notification.markdownBody} />}
      userAvatar={currentUser}
      sport={sport}
      createdAt={createdAt}
      link={link}
      read={read}
      {...rest}
    />
  );
};

InGameCurrencyNotification.fragments = {
  inGameCurrencyNotification: gql`
    fragment InGameCurrencyNotification_inGameCurrencyNotification on InGameCurrencyNotification {
      id
      ...Notification_notificationInterface
      inGameCurrencyBalance {
        currency
      }
      sport
    }
    ${commonNotificationInterfaceFragment}
  ` as TypedDocumentNode<InGameCurrencyNotification_inGameCurrencyNotification>,
};
